import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, BehaviorSubject, interval, Subscription } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { SignalRHubService } from "./signalr-hub.service";

/** Models */
export interface NotificationDto {
  id: number;
  title: string;
  content: string;
  notificationType: "Info" | "Success" | "Warning" | "Error";
  entityType?: string;
  entityId?: string;
  metaData?: string;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
  timeAgo: string;
  // Compatibility properties for old template
  daDoc: boolean;
  tieuDe: string;
  noiDung: string;
  createTimeStr: string;
}

export interface NotificationListResponse {
  Notifications?: NotificationDto[];
  notifications?: NotificationDto[];
  UnreadCount?: number;
  unreadCount?: number;
  TotalCount?: number;
  totalCount?: number;
  PageIndex?: number;
  pageIndex?: number;
  PageSize?: number;
  pageSize?: number;
}

export interface UnreadCountResponse {
  Count?: number;
  count?: number;
}

/**
 * Service quản lý Notification
 * - Gọi API lấy danh sách thông báo
 * - Quản lý unread count
 * - Hỗ trợ polling/realtime updates
 */
@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private apiUrl = `${environment.apiUri}/api/notification`;

  // BehaviorSubject để share data giữa các components
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<NotificationDto[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  // Polling subscription
  private pollingSubscription?: Subscription;

  constructor(
    private http: HttpClient,
    private signalRHub: SignalRHubService
  ) {
    // Khởi động SignalR connection
    this.initializeSignalR();
  }

  /**
   * Khởi tạo SignalR và lắng nghe notifications real-time
   */
  private initializeSignalR(): void {
    // Lắng nghe notification mới từ SignalR
    this.signalRHub.onNotificationReceived.subscribe((payload: any) => {
      console.log('🔔 Real-time notification payload:', payload);

      // Map SignalR PascalCase properties to camelCase NotificationDto
      const notification: NotificationDto = {
        id: payload.Id || payload.id,
        title: payload.Title || payload.title,
        content: payload.Content || payload.content,
        notificationType: payload.NotificationType || payload.type || 'Info',
        entityType: payload.EntityType,
        entityId: payload.EntityId,
        metaData: payload.MetaData,
        createdAt: payload.CreatedAt ? new Date(payload.CreatedAt) : new Date(),
        isRead: false,
        timeAgo: 'Vừa xong',
        // Compatibility properties
        daDoc: false,
        tieuDe: payload.Title || payload.title,
        noiDung: payload.Content || payload.content,
        createTimeStr: 'Vừa xong'
      };

      // Thêm notification vào đầu danh sách hiện tại
      const currentNotifications = this.notificationsSubject.value;
      this.notificationsSubject.next([notification, ...currentNotifications]);

      // Tăng unread count thông qua việc gọi API hoặc cộng thủ công
      // Ở đây ta cộng thủ công để update UI tức thì
      this.unreadCountSubject.next(this.unreadCountSubject.value + 1);

      // Hiển thị toast
      this.showToast(notification);
    });

    // Lắng nghe trạng thái kết nối
    this.signalRHub.onConnectionChanged.subscribe((isConnected) => {
      // Nếu reconnected, refresh count
      if (isConnected) {
        this.getUnreadCount().subscribe();
      }
    });
  }

  /**
   * Hiển thị toast notification (tùy chọn)
   */
  private showToast(notification: any): void {
    // Có thể dùng library như ng-toast, ngx-toastr, hoặc custom
    console.log('📬 New notification:', notification.title);
  }

  /**
   * Lấy danh sách thông báo (có phân trang)
   */
  getNotifications(
    pageIndex: number = 1,
    pageSize: number = 20,
    onlyUnread: boolean = false,
  ): Observable<NotificationListResponse> {
    return this.http
      .get<any>(`${this.apiUrl}/list`, {
        params: {
          pageIndex: pageIndex.toString(),
          pageSize: pageSize.toString(),
          onlyUnread: onlyUnread.toString(),
        },
      })
      .pipe(
        tap((response) => {
          // Normalize PascalCase from backend and map items to camelCase
          const rawNotifications = response.Notifications || response.notifications || [];
          const notifications: NotificationDto[] = rawNotifications.map((n: any) => ({
            id: n.Id || n.id,
            title: n.Title || n.title,
            content: n.Content || n.content,
            notificationType: n.NotificationType || n.notificationType || n.type || 'Info',
            entityType: n.EntityType || n.entityType,
            entityId: n.EntityId || n.entityId,
            metaData: n.MetaData || n.metaData,
            createdAt: n.CreatedAt || n.createdAt ? new Date(n.CreatedAt || n.createdAt) : new Date(),
            isRead: n.IsRead !== undefined ? n.IsRead : (n.daDoc !== undefined ? n.daDoc : false),
            timeAgo: n.TimeAgo || n.timeAgo || 'Vừa xong',
            // Compatibility properties
            daDoc: n.IsRead !== undefined ? n.IsRead : (n.daDoc !== undefined ? n.daDoc : false),
            tieuDe: n.Title || n.title,
            noiDung: n.Content || n.content,
            createTimeStr: n.TimeAgo || n.timeAgo || 'Vừa xong'
          }));

          const unreadCount = response.UnreadCount || response.unreadCount || 0;

          if (pageIndex === 1) {
            this.notificationsSubject.next(notifications);
          } else {
            const current = this.notificationsSubject.value;
            this.notificationsSubject.next([...current, ...notifications]);
          }
          this.unreadCountSubject.next(unreadCount);
        }),
      );
  }

  /**
   * Đếm số thông báo chưa đọc
   */
  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http
      .get<UnreadCountResponse>(`${this.apiUrl}/unread-count`)
      .pipe(
        tap((response) => {
          this.unreadCountSubject.next(response.Count || response.count || 0);
        }),
      );
  }

  /**
   * Đánh dấu đã đọc
   * @param notificationId - null để đánh dấu tất cả
   */
  markAsRead(notificationId?: number): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/mark-as-read`, { NotificationId: notificationId })
      .pipe(
        tap(() => {
          // Refresh count sau khi mark as read
          this.getUnreadCount().subscribe();
        }),
      );
  }

  /**
   * Xóa thông báo
   */
  deleteNotification(notificationId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/delete`, { NotificationId: notificationId });
  }

  /**
   * Xóa nhiều thông báo
   */
  deleteBulk(notificationIds: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/delete-bulk`, { NotificationIds: notificationIds });
  }

  /**
   * Lấy chi tiết 1 thông báo
   */
  getNotificationDetail(id: number): Observable<NotificationDto> {
    return this.http.get<NotificationDto>(`${this.apiUrl}/detail/${id}`);
  }

  /**
   * Bắt đầu polling để cập nhật count định kỳ (mỗi 2 phút - SignalR là chính)
   */
  startPolling(intervalMs: number = 120000): void {
    if (this.pollingSubscription) {
      return; // Đã chạy rồi
    }

    this.pollingSubscription = interval(intervalMs).subscribe(() => {
      this.getUnreadCount().subscribe();
    });
  }

  /**
   * Dừng polling
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  /**
   * Helper: Get type icon class
   */
  getIconClass(type: string): string {
    const iconMap = {
      Info: "fa fa-info-circle text-primary",
      Success: "fa fa-check-circle text-success",
      Warning: "fa fa-exclamation-triangle text-warning",
      Error: "fa fa-times-circle text-danger",
    };
    return iconMap[type] || iconMap["Info"];
  }
}
